/*
 * Copyright (c) Microsoft. All rights reserved. Licensed under the MIT license. See full license in root of repo. -->
 *
 * This file shows how to use MSAL.js to get an access token to your server and pass it to the task pane.
 */

import { InteractionRequiredAuthError, LogLevel, PublicClientApplication } from "@azure/msal-browser";
import { getMessagesList } from "./middle-tier-calls";
import { ConsoleLogger } from "./error-handler";

const clientId = process.env.CLIENT_ID; //This is your client ID
const accessScope = `api://${window.location.host}/${clientId}/access_as_user`;
const accessTokenRequest = {
  scopes: [accessScope],
  // Match the manifest's WebApplicationInfo scopes. Without Mail.Read.Shared here,
  // users who hit the dialog-based fallback path get a token without shared-folder
  // access, and the subsequent /users/{owner}/messages Graph call 403s.
  extraScopesToConsent: ["Mail.Read", "Mail.Read.Shared"],
};

const msalConfig = {
  auth: {
    clientId: clientId,
    authority: `https://login.microsoftonline.com/common`,
    redirectUri: process.env.REDIRECT_URI,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: "localStorage", // Needed to avoid "User login is required" error.
    storeAuthStateInCookie: true, // Recommended to avoid certain IE/Edge issues.
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii || process.env.ENV === "production") {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

const publicClientApp = new PublicClientApplication(msalConfig);

let callbackFunction = null;
let conversationId = null;
let mailboxOwnerEmail = null;
let loginDialog = null;

Office.onReady(async () => {
  if (Office.context.ui.messageParent) {
    publicClientApp
      .handleRedirectPromise()
      .then(handleResponse)
      .catch((error) => {
        ConsoleLogger(error, "handleRedirectPromise catch");
        Office.context.ui.messageParent(JSON.stringify({ status: "failure", result: error }));
      });

    // The very first time the add-in runs on a developer's computer, msal.js hasn't yet
    // stored login data in localStorage. So a direct call of acquireTokenRedirect
    // causes the error "User login is required". Once the user is logged in successfully
    // the first time, msal data in localStorage will prevent this error from ever hap-
    // pening again; but the error must be blocked here, so that the user can login
    // successfully the first time. To do that, call loginRedirect first instead of
    // acquireTokenRedirect.
    if (localStorage.getItem("loggedIn") === "yes") {
      publicClientApp.acquireTokenRedirect(accessTokenRequest);
    } else {
      // This will login the user and then the (response.tokenType === "id_token")
      // path in authCallback below will run, which sets localStorage.loggedIn to "yes"
      // and then the dialog is redirected back to this script, so the
      // acquireTokenRedirect above runs.
      publicClientApp.loginRedirect(accessTokenRequest);
    }
  }
});

function handleResponse(response) {
  if (response.tokenType === "id_token") {
    localStorage.setItem("loggedIn", "yes");
  } else {
    Office.context.ui.messageParent(
      JSON.stringify({
        status: "success",
        result: response.accessToken,
        accountId: response.account.homeAccountId,
        homeAccount: response.account,
      })
    );
  }
}

export async function dialogFallback(id, ownerEmail, callback) {
  conversationId = id;
  mailboxOwnerEmail = ownerEmail;
  callbackFunction = callback;
  try {
    // Acquire token silent
    const result = await publicClientApp.acquireTokenSilent(accessTokenRequest);
    if (result !== null && result.accessToken !== null) {
      const response = await getMessagesList(result.accessToken, conversationId, mailboxOwnerEmail);

      ConsoleLogger(response, "dialogFallback");

      // For cases when getMessagesList throw errors like "invalid_grant"
      // clear the account cache in localStorage
      if (response?.error) {
        ConsoleLogger(response, "dialogFallback getMessagesList error");
        showLoginPopup();
        return;
      }

      callbackFunction(response);
    }
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      showLoginPopup();
      return;
    }

    ConsoleLogger(error, "dialogFallback catch");

    if (error?.errorCode && ["no_account_error", "monitor_window_timeout"].includes(error?.errorCode)) {
      showLoginPopup();
      return;
    }

    callbackFunction({ error: error?.message || "Fallback auth error" });
  }
}

// Open a pop-up and display the sign-in page for the identity provider.
// async function showLoginPopup() {
//   try {
//     const response = await publicClientApp.acquireTokenPopup(accessTokenRequest);

//     // Configure MSAL to use the signed-in account as the active account for future requests.
//     const homeAccount = publicClientApp.getAccountByHomeId(response.account.homeAccountId);

//     if (homeAccount) {
//       publicClientApp.setActiveAccount(homeAccount);
//     }

//     const messageListResponse = await getMessagesList(response.accessToken, conversationId);
//     ConsoleLogger(messageListResponse, 'showLoginPopup');

//     callbackFunction(messageListResponse);
//   } catch (error) {
//     ConsoleLogger(error, 'showLoginPopup catch');
//     callbackFunction({ error: error?.message || "Login popup error" });
//   }
// }

// This handler responds to the success or failure message that the pop-up dialog receives from the identity provider
// and access token provider.
async function processMessage(arg) {
  ConsoleLogger(arg, "Message received in processMessage");

  let messageFromDialog = JSON.parse(arg.message);

  if (messageFromDialog.status === "success") {
    // We now have a valid access token.
    loginDialog.close();

    const homeAccountId = messageFromDialog?.accountId;
    const homeAccount = publicClientApp.getAccountByHomeId(homeAccountId);

    ConsoleLogger(homeAccount, "MSAL getAccountByHomeId");

    // Set the active account so future token requests can be silent.
    if (homeAccount) {
      publicClientApp.setActiveAccount(homeAccount);
    }

    const response = await getMessagesList(messageFromDialog.result, conversationId, mailboxOwnerEmail);

    ConsoleLogger(response, "processMessage getMessagesList");

    callbackFunction(response);
  } else if (messageFromDialog.error === undefined && messageFromDialog.result.errorCode === undefined) {
    // Need to pick the user to use to auth
  } else {
    // Something went wrong with authentication or the authorization of the web application.
    loginDialog.close();
    if (messageFromDialog.error) {
      callbackFunction({ error: JSON.stringify(messageFromDialog.error.toString()) });
    } else if (messageFromDialog.result) {
      callbackFunction({ error: JSON.stringify(messageFromDialog.result.errorMessage.toString()) });
    }
  }
}

// Use the Office dialog API to open a pop-up and display the sign-in page for the identity provider.
function showLoginPopup() {
  const url = "/fallbackauthdialog.html";
  const fullUrl = location.protocol + "//" + location.hostname + (location.port ? ":" + location.port : "") + url;

  // height and width are percentages of the size of the parent Office application, e.g., PowerPoint, Excel, Word, etc.
  Office.context.ui.displayDialogAsync(fullUrl, { height: 60, width: 30, promptBeforeOpen: false }, function (result) {
    ConsoleLogger("Dialog has initialized. Wiring up events", "showLoginPopup");
    loginDialog = result.value;

    loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, processMessage);

    loginDialog.addEventHandler(Office.EventType.DialogEventReceived, (arg) => {
      switch (arg?.error) {
        case 12002:
          callbackFunction({
            error:
              "The dialog box has been directed to a page that it cannot find or load, or the URL syntax is invalid.",
          });
          break;
        case 12003:
          callbackFunction({
            error: "The dialog box has been directed to a URL with the HTTP protocol. HTTPS is required.",
          });
          break;
        case 12006:
          callbackFunction({ error: "Dialog closed." });
          break;
        default:
          ConsoleLogger("Unknown error in dialog box.");
          callbackFunction({ error: "Unknown error in dialog box." });
          break;
      }

      ConsoleLogger(arg, "DialogEventReceived");
    });
  });
}
